<template>
  <b-modal
    id="tradie-edit-timesheet-modal"
    modal-class="timesheet-modal"
    :title="isEdit ? 'Edit Entry' : 'Add Entry'"
    :ok-title="isEdit ? 'Save' : 'Add'"
    :ok-disabled="!canSave || submitting"
    :busy="submitting"
    @ok.prevent="submit"
    @hidden="reset"
    @shown="onShown"
  >
    <div
      v-if="isEdit && existing && existing.admin_comment"
      class="rejection-reason mb-3"
    >
      <strong>Rejection reason</strong>
      <div>{{ existing.admin_comment }}</div>
    </div>

    <b-form-group label="Project">
      <b-form-input
        v-if="isEdit"
        :value="existing && existing.project && existing.project.name"
        disabled
      />
      <div v-else-if="!projects.length" class="text-muted small">
        No projects available
      </div>
      <b-form-select v-else v-model="projectId" :options="projectOptions" />
    </b-form-group>

    <div class="d-flex entry-cards">
      <b-form-group label="Clock-in date" class="mr-2">
        <b-form-datepicker
          v-model="inDate"
          :max="today"
          boundary="window"
          locale="en-AU"
          today-button
          reset-button
          close-button
        />
      </b-form-group>
      <b-form-group label="Clock-in time">
        <datetime-input v-model="inTime" type="time" />
      </b-form-group>
    </div>

    <div class="d-flex entry-cards">
      <b-form-group label="Clock-out date" class="mr-2">
        <b-form-datepicker
          :value="outDate"
          :min="inDate || undefined"
          :max="today"
          :initial-date="outDate || inDate || undefined"
          boundary="window"
          locale="en-AU"
          today-button
          reset-button
          close-button
          @input="onOutDateInput"
        />
      </b-form-group>
      <b-form-group label="Clock-out time">
        <datetime-input v-model="outTime" type="time" />
      </b-form-group>
    </div>

    <p v-if="isOvernight" class="overnight-hint">
      Overnight shift — finishes the next day.
    </p>

    <b-alert :show="!!timeError" variant="danger">{{ timeError }}</b-alert>

    <b-form-group label="Break (minutes)">
      <b-form-input v-model.number="breakMinutes" type="number" min="0" />
    </b-form-group>

    <b-form-group label="Comment">
      <b-form-textarea v-model="comment" maxlength="500" rows="3" />
    </b-form-group>

    <b-form-group label="Photos">
      <div class="photo-thumbs mb-2" v-if="existingPhotos.length">
        <div
          v-for="photo in existingPhotos"
          :key="photo.id"
          class="photo-thumb-wrap"
        >
          <b-icon-x-circle-fill
            variant="dark"
            class="remove-icon"
            @click="removeExistingPhoto(photo)"
          />
          <b-img :src="photo.url" class="photo-thumb" />
        </div>
      </div>
      <clock-out-photo-upload v-model="newPhotos" />
    </b-form-group>

    <b-form-group label="Plant / Equipment used" v-if="equipmentOptions.length">
      <plant-entry-row
        v-for="(entry, i) in plantEntries"
        :key="i"
        :value="entry"
        :equipment-options="equipmentChoices"
        :min-quantity="1"
        :taken-ids="takenIdsFor(i)"
        :disabled="submitting"
        @input="updatePlantEntry(i, $event)"
        @remove="removePlantEntry(i)"
      />
      <b-button
        variant="outline-secondary"
        size="sm"
        :disabled="allEquipmentAdded"
        @click="addPlantEntry"
      >
        + Add equipment
      </b-button>
    </b-form-group>

    <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import {
  combineBusinessDateTime,
  splitBusinessIso,
  formatBusinessTime,
  bizNow,
  clampPlantQuantity,
  plantEntryErrorMessage,
} from '@/utils/timesheetHelpers'
import PlantEntryRow from '@/components/timesheets/shared/PlantEntryRow'
import DatetimeInput from '@/components/timesheets/DatetimeInput'
import ClockOutPhotoUpload from './ClockOutPhotoUpload'

function addDay(ymd) {
  const d = new Date(`${ymd}T00:00:00`)
  d.setDate(d.getDate() + 1)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'TradieEditTimesheetDialog',

  components: { PlantEntryRow, DatetimeInput, ClockOutPhotoUpload },

  props: {
    // The entry being edited, or null when adding a new one.
    existing: { type: Object, default: null },
    projects: { type: Array, default: () => [] },
  },

  data() {
    return {
      projectId: null,
      inDate: '',
      inTime: '',
      outDate: '',
      outTime: '',
      outDateManual: false,
      breakMinutes: 0,
      comment: '',
      existingPhotos: [],
      photosToDelete: [],
      newPhotos: [],
      plantEntries: [],
      equipmentOptions: [],
      timeError: null,
      submitting: false,
      error: null,
    }
  },

  computed: {
    isEdit() {
      return !!this.existing
    },

    // Ceiling for both date pickers. Timesheet dates are job-site wall clock,
    // so "today" is today at the site, not in the viewer's own timezone. The
    // BA add-entry modal already caps its pickers the same way; this dialog
    // never did, which let a tradie log a shift days in the future.
    today() {
      return bizNow(this.resolvedTz).format('YYYY-MM-DD')
    },

    // The event's own stamped timezone when editing (never reinterpreted —
    // see docs/designs/per-site-timezones.md), or the selected project's
    // resolved timezone for a new entry. myEditEvent()/myManualEntry() both
    // validate `clocked_in_at`/`clocked_out_at` as a full ISO instant
    // (TimeSheetController.php:2160-2179, 2354-2374 in the API), not a
    // naive local string, so — unlike the BA-side editEvent()/
    // adminClockOut() paths, deliberately left Perth-only this phase —
    // it's safe for the tradie's own entry to use the real site timezone.
    resolvedTz() {
      if (this.existing) return this.existing.timezone
      const project = this.projects.find((p) => p.id === this.projectId)
      return project && project.timezone
    },

    // Each item can be added once, so no more rows once every item is on one.
    allEquipmentAdded() {
      return this.plantEntries.length >= this.equipmentOptions.length
    },

    equipmentChoices() {
      return [
        { value: null, text: 'Select equipment' },
        ...this.equipmentOptions,
      ]
    },

    projectOptions() {
      return this.projects.map((p) => ({ value: p.id, text: p.name }))
    },

    // A clock-out time at or before the clock-in time means the shift crossed
    // midnight, so the clock-out date follows to the next day (same as the app).
    autoOutDate() {
      if (!this.inDate) return ''
      const crossesMidnight =
        !!this.inTime && !!this.outTime && this.outTime <= this.inTime
      return crossesMidnight ? addDay(this.inDate) : this.inDate
    },

    isOvernight() {
      return !!this.outDate && !!this.inDate && this.outDate > this.inDate
    },

    canSave() {
      const hasProject = this.isEdit || !!this.projectId
      return (
        hasProject &&
        !!this.inDate &&
        !!this.inTime &&
        !!this.outDate &&
        !!this.outTime
      )
    },
  },

  watch: {
    autoOutDate(value) {
      if (!this.outDateManual && value) this.outDate = value
    },

    // A new entry's equipment list belongs to the chosen project.
    projectId() {
      if (this.isEdit) return
      this.plantEntries = []
      this.loadEquipment()
    },
  },

  methods: {
    ...mapActions('timesheet', ['myEditEvent', 'myManualEntry']),
    ...mapActions('plantEquipment', ['getMyPlantEquipment']),

    onOutDateInput(value) {
      if (value !== this.autoOutDate) this.outDateManual = true
      this.outDate = value
    },

    async onShown() {
      if (this.existing) {
        this.outDateManual = true
        const inParts = splitBusinessIso(
          this.existing.selected_clocked_in_at || this.existing.clocked_in_at,
          this.resolvedTz,
        )
        const outParts = splitBusinessIso(
          this.existing.selected_clocked_out_at || this.existing.clocked_out_at,
          this.resolvedTz,
        )
        this.inDate = inParts.date
        this.inTime = inParts.time
        this.outDate = outParts.date
        this.outTime = outParts.time
        this.breakMinutes = this.existing.break_minutes || 0
        this.comment = this.existing.comment || ''
        this.existingPhotos = (this.existing.photo_urls || []).slice()
        this.plantEntries = (this.existing.plant_entries || []).map((pe) => ({
          plant_equipment_id: pe.plant_equipment_id,
          quantity: pe.quantity,
        }))
      } else if (this.projects.length === 1) {
        this.projectId = this.projects[0].id
      }

      if (this.existing) await this.loadEquipment()
    },

    // /plant-equipment/mine needs the project; without it the API returns 422.
    async loadEquipment() {
      const projectId = this.existing
        ? this.existing.project_id
        : this.projectId
      if (!projectId) {
        this.equipmentOptions = []
        return
      }
      try {
        const items = await this.getMyPlantEquipment({ project_id: projectId })
        this.equipmentOptions = items.map((i) => ({
          value: i.id,
          text: i.name,
          rateType: i.rate_type,
        }))
      } catch (e) {
        this.equipmentOptions = []
      }
    },

    removeExistingPhoto(photo) {
      this.photosToDelete.push(photo.id)
      this.existingPhotos = this.existingPhotos.filter((p) => p.id !== photo.id)
    },

    addPlantEntry() {
      this.plantEntries.push({ plant_equipment_id: null, quantity: 1 })
    },

    // Items picked on the other rows, which this row can no longer choose.
    takenIdsFor(index) {
      return this.plantEntries
        .filter((e, i) => i !== index && e.plant_equipment_id)
        .map((e) => e.plant_equipment_id)
    },

    // Rows with an item picked, with amounts held to 1 - 999.99.
    cleanPlantEntries() {
      return this.plantEntries
        .filter((e) => e.plant_equipment_id)
        .map((e) => ({
          plant_equipment_id: e.plant_equipment_id,
          quantity: clampPlantQuantity(e.quantity, 1),
        }))
    },

    updatePlantEntry(index, value) {
      this.plantEntries.splice(index, 1, value)
    },

    removePlantEntry(index) {
      this.plantEntries.splice(index, 1)
    },

    buildFormData(clockedInIso, clockedOutIso, confirmedOverlap) {
      const formData = new FormData()
      formData.append('clocked_in_at', clockedInIso)
      formData.append('clocked_out_at', clockedOutIso)
      formData.append('selected_clocked_in_at', clockedInIso)
      formData.append('selected_clocked_out_at', clockedOutIso)
      formData.append('break_minutes', this.breakMinutes || 0)
      if (this.comment.trim()) formData.append('comment', this.comment.trim())
      if (!this.isEdit) formData.append('project_id', this.projectId)
      // '1', not 'true' — Laravel's boolean validation rule rejects 'true'.
      if (confirmedOverlap) formData.append('confirmed_overlap', '1')
      this.newPhotos.forEach((file, i) => {
        formData.append(`photos[${i}]`, file, file.name)
      })
      this.photosToDelete.forEach((id, i) => {
        formData.append(`photos_to_delete[${i}]`, id)
      })
      this.cleanPlantEntries().forEach((e, i) => {
        formData.append(
          `plant_entries[${i}][plant_equipment_id]`,
          e.plant_equipment_id,
        )
        formData.append(`plant_entries[${i}][quantity]`, e.quantity)
      })
      return formData
    },

    buildPayload(clockedInIso, clockedOutIso, confirmedOverlap) {
      const hasFiles =
        this.newPhotos.length > 0 || this.photosToDelete.length > 0
      if (hasFiles) {
        return this.buildFormData(clockedInIso, clockedOutIso, confirmedOverlap)
      }
      return {
        ...(this.isEdit ? {} : { project_id: this.projectId }),
        clocked_in_at: clockedInIso,
        clocked_out_at: clockedOutIso,
        selected_clocked_in_at: clockedInIso,
        selected_clocked_out_at: clockedOutIso,
        break_minutes: this.breakMinutes || 0,
        comment: this.comment.trim() || null,
        plant_entries: this.cleanPlantEntries(),
        ...(confirmedOverlap ? { confirmed_overlap: true } : {}),
      }
    },

    send(clockedInIso, clockedOutIso, confirmedOverlap) {
      const payload = this.buildPayload(
        clockedInIso,
        clockedOutIso,
        confirmedOverlap,
      )
      if (this.isEdit) {
        return this.myEditEvent({ id: this.existing.id, data: payload })
      }
      return this.myManualEntry(payload)
    },

    // Mirrors Flutter's _confirmOverlap wording — the API's 409 means the
    // tradie has an active GPS clock-in that this entry would overlap.
    async confirmOverlap(errorData) {
      const activeAt = errorData.active_clocked_in_at
      const label = errorData.active_label
      const actionLabel = this.isEdit ? 'Save anyway' : 'Add anyway'
      const message = activeAt
        ? `You clocked in at ${
            label || 'your current site'
          } at ${formatBusinessTime(
            activeAt,
          )}. When you clock out, those hours will be added automatically — this entry may double-count your time.`
        : 'You are currently clocked in. When you clock out, those hours will be added automatically - this entry may double-count your time.'

      return this.$bvModal.msgBoxConfirm(message, {
        title: "You're still clocked in",
        okTitle: actionLabel,
        cancelTitle: 'Cancel',
      })
    },

    async submit() {
      if (!this.canSave || this.submitting) return

      const clockedInIso = combineBusinessDateTime(
        this.inDate,
        this.inTime,
        this.resolvedTz,
      )
      const clockedOutIso = combineBusinessDateTime(
        this.outDate,
        this.outTime,
        this.resolvedTz,
      )
      if (new Date(clockedOutIso) <= new Date(clockedInIso)) {
        this.timeError = 'Clock-out must be after clock-in'
        return
      }
      // The pickers already cap at today, but a same-day clock-out time can
      // still land in the future, and a typed date bypasses :max entirely.
      if (new Date(clockedOutIso).getTime() > Date.now()) {
        this.timeError = "Clock-out can't be in the future"
        return
      }
      this.timeError = null
      this.submitting = true
      this.error = null

      try {
        let event
        try {
          event = await this.send(clockedInIso, clockedOutIso, false)
        } catch (e) {
          const data = e.response && e.response.data
          if (
            e.response &&
            e.response.status === 409 &&
            data &&
            data.error_code === 'active_clock_in_overlap'
          ) {
            const proceed = await this.confirmOverlap(data)
            if (!proceed) {
              this.submitting = false
              return
            }
            event = await this.send(clockedInIso, clockedOutIso, true)
          } else {
            throw e
          }
        }
        this.$emit('saved', event)
        this.$bvModal.hide('tradie-edit-timesheet-modal')
      } catch (e) {
        const data = e.response && e.response.data
        this.error =
          plantEntryErrorMessage(data) ||
          (data && data.message) ||
          'Something went wrong saving this entry - try again.'
      } finally {
        this.submitting = false
      }
    },

    reset() {
      this.projectId = null
      this.inDate = ''
      this.inTime = ''
      this.outDate = ''
      this.outTime = ''
      this.outDateManual = false
      this.breakMinutes = 0
      this.comment = ''
      this.existingPhotos = []
      this.photosToDelete = []
      this.newPhotos = []
      this.plantEntries = []
      this.timeError = null
      this.error = null
    },
  },
}
</script>

<style lang="scss" scoped>
.rejection-reason {
  background: #fef2f2;
  color: #991b1b;
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
}

.overnight-hint {
  font-size: 12.5px;
  color: #6b7280;
  margin: -6px 0 12px;
}

.photo-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.photo-thumb-wrap {
  position: relative;
  width: 60px;
  height: 60px;
}

.photo-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.remove-icon {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 1;
  cursor: pointer;
  background: #fff;
  border-radius: 50%;
}
</style>
